// frontend/src/components/admin/DanicedSettings.jsx

import { useState, useEffect } from 'react';
import {
  Building2, Phone, Mail, Globe, MapPin, User,
  Save, CheckCircle2, AlertCircle, FileText,
  CreditCard, RefreshCw, Info, Headphones, Upload,
  Plus, Trash2, PenLine, GripVertical, Stamp,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';
import { API_URL } from '../../config/apiBase';

// ============================================================================
// SOUS-COMPOSANTS UTILITAIRES
// ============================================================================

const Field = ({ label, value, onChange, placeholder, type = 'text', icon: Icon, theme: t, half }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: Icon ? '9px 12px 9px 34px' : '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, fontWeight: 500, boxSizing: 'border-box', outline: 'none' }}
        onFocus={e => { e.target.style.borderColor = t.primary; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
        onBlur={e  => { e.target.style.borderColor = t.border;  e.target.style.boxShadow = 'none'; }} />
    </div>
  </div>
);

const TextareaField = ({ label, value, onChange, placeholder, rows = 3, theme: t, half }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>{label}</label>
    <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
      onFocus={e => { e.target.style.borderColor = t.primary; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
      onBlur={e  => { e.target.style.borderColor = t.border;  e.target.style.boxShadow = 'none'; }} />
  </div>
);

const SectionHeader = ({ icon: Icon, title, sub, theme: t }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: t.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={17} style={{ color: t.primary }} />
    </div>
    <div>
      <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>{title}</p>
      {sub && <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{sub}</p>}
    </div>
  </div>
);

const TarifCard = ({ type, label, prix, onChange, theme: t }) => {
  const COLORS = {
    basique:  { color: '#3b82f6', bg: 'rgba(59,130,246,0.07)',  border: 'rgba(59,130,246,0.2)'  },
    standard: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.07)', border: 'rgba(139,92,246,0.2)'  },
    premium:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.07)', border: 'rgba(245,158,11,0.2)'  },
  };
  const c = COLORS[type] || COLORS.basique;
  return (
    <div style={{ padding: '16px 18px', borderRadius: 14, background: c.bg, border: `1px solid ${c.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 800, color: c.color, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 2px' }}>{type}</p>
          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>{label}</p>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: c.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CreditCard size={16} style={{ color: c.color }} />
        </div>
      </div>
      <label style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, display: 'block', marginBottom: 5 }}>Prix mensuel (FCFA)</label>
      <input type="number" min={0} step={1000} value={prix || 0} onChange={e => onChange(parseFloat(e.target.value) || 0)}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: t.bgCard, border: `1px solid ${c.border}`, color: t.text, fontSize: 16, fontWeight: 800, textAlign: 'right', boxSizing: 'border-box', outline: 'none' }}
        onFocus={e => { e.target.style.borderColor = c.color; e.target.style.boxShadow = `0 0 0 3px ${c.color}18`; }}
        onBlur={e  => { e.target.style.borderColor = c.border; e.target.style.boxShadow = 'none'; }} />
      <p style={{ fontSize: 11, color: c.color, fontWeight: 700, textAlign: 'right', margin: '6px 0 0' }}>
        {(prix || 0).toLocaleString('fr-FR')} FCFA / mois
      </p>
    </div>
  );
};

// Bloc upload image générique
const ImageUploadBlock = ({ label, sublabel, preview, uploading, onUpload, inputId, theme: t, accentColor, size = 100 }) => {
  const accent = accentColor || t.primary;
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 10 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: size, height: size, borderRadius: 12, border: `2px dashed ${t.border}`, background: t.bgInput, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
          {uploading
            ? <span style={{ width: 24, height: 24, borderRadius: '50%', border: `3px solid ${accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
            : preview
              ? <img src={preview} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <div style={{ textAlign: 'center', padding: 8 }}>
                  <Upload size={18} style={{ color: t.textMuted, display: 'block', margin: '0 auto 4px' }} />
                  <span style={{ fontSize: 10, color: t.textMuted }}>Aucun fichier</span>
                </div>
          }
        </div>
        <div>
          <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onUpload} style={{ display: 'none' }} id={inputId} disabled={uploading} />
          <label htmlFor={inputId} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, background: accent + '15', color: accent, fontSize: 12, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
            <Upload size={13} /> {uploading ? 'Upload...' : 'Choisir'}
          </label>
          {sublabel && <p style={{ fontSize: 10, color: t.textMuted, marginTop: 5 }}>{sublabel}</p>}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// GESTIONNAIRE MODES DE PAIEMENT
// ============================================================================

const PaymentMethodsManager = ({ theme: t }) => {
  const [methods,  setMethods]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({ libelle: '', numero_rib: '', instructions: '', ordre: 0, actif: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.danicedSettings.getPaymentMethods();
      if (res.success) setMethods(res.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ libelle: '', numero_rib: '', instructions: '', ordre: methods.length, actif: 1 });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (m) => {
    setForm({ libelle: m.libelle, numero_rib: m.numero_rib || '', instructions: m.instructions || '', ordre: m.ordre, actif: m.actif });
    setEditId(m.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.libelle.trim()) return;
    setSaving(true);
    try {
      if (editId) await superAdminApi.danicedSettings.updatePaymentMethod(editId, form);
      else        await superAdminApi.danicedSettings.createPaymentMethod(form);
      await load();
      setShowForm(false);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce mode de paiement ?')) return;
    await superAdminApi.danicedSettings.deletePaymentMethod(id);
    await load();
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>Chargement…</div>
      ) : methods.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', borderRadius: 12, border: `2px dashed ${t.border}`, color: t.textMuted, fontSize: 13, marginBottom: 14 }}>
          Aucun mode de paiement configuré
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {methods.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', borderRadius: 12, background: t.bgInput, border: `1px solid ${t.border}`, opacity: m.actif ? 1 : 0.55 }}>
              <GripVertical size={16} style={{ color: t.textMuted, marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{m.libelle}</span>
                  {!m.actif && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: t.border, color: t.textMuted, fontWeight: 700 }}>Inactif</span>}
                </div>
                {m.numero_rib    && <div style={{ fontSize: 12, color: t.primary, fontWeight: 600 }}>{m.numero_rib}</div>}
                {m.instructions  && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{m.instructions}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => openEdit(m)} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textSecondary, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <PenLine size={12} /> Modifier
                </button>
                <button onClick={() => handleDelete(m.id)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.05)', color: '#ef4444', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!showForm && (
        <button onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 10, border: `2px dashed ${t.primary}`, background: t.primarySoft, color: t.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%', justifyContent: 'center' }}>
          <Plus size={15} /> Ajouter un mode de paiement
        </button>
      )}

      {showForm && (
        <div style={{ padding: '18px 20px', borderRadius: 14, border: `1px solid ${t.primary}`, background: t.primarySofter, marginTop: 4 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 14px' }}>
            {editId ? 'Modifier le mode de paiement' : 'Nouveau mode de paiement'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Libellé *</label>
              <input value={form.libelle} onChange={e => setF('libelle', e.target.value)} placeholder="Ex: Mobile Money MTN, Virement bancaire BNI..."
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Numéro / RIB / IBAN</label>
              <input value={form.numero_rib} onChange={e => setF('numero_rib', e.target.value)} placeholder="Ex: +242 06 695 74 26  ou  FR76 1234 5678..."
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Instructions (optionnel)</label>
              <textarea rows={2} value={form.instructions} onChange={e => setF('instructions', e.target.value)} placeholder="Ex: Indiquer le numéro de facture en référence..."
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Ordre d'affichage</label>
                <input type="number" min={0} value={form.ordre} onChange={e => setF('ordre', parseInt(e.target.value) || 0)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input type="checkbox" id="pm-actif" checked={form.actif == 1} onChange={e => setF('actif', e.target.checked ? 1 : 0)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <label htmlFor="pm-actif" style={{ fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer' }}>Actif</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '8px 18px', borderRadius: 10, border: `1px solid ${t.border}`, background: 'transparent', color: t.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving || !form.libelle.trim()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 10, background: t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <CheckCircle2 size={14} />}
                {saving ? 'Sauvegarde...' : editId ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const DanicedSettings = () => {
  const t = useTheme();

  const [settings, setSettings] = useState({
    company_name: 'DANICED GROUP', slogan: '',
    adresse: '', adresse_europe: '', telephone_europe: '',
    adresse_afrique: '', telephone_afrique: '',
    ville: '', pays: 'Congo',
    telephone: '', telephone2: '', email: '', site_web: '',
    interlocuteur: '', interlocuteur_titre: '',
    support_name: '', support_phone: '', support_email: '',
    numero_registre: '', mentions_legales: '',
    invoice_prefix: 'INV', devise: 'FCFA',
    horapilot_product_name: 'HoraPilot',
    fiche_tagline:      '',
    fiche_presentation: '',
    fiche_pour_qui:     '',
    fiche_cta:          '',
    fiche_screenshot_1:  '',
    fiche_screenshot_2:  '',
  });

  const [tarifs, setTarifs] = useState({
    basique:  { prix: 0, label: 'HoraPilot Basique'  },
    standard: { prix: 0, label: 'HoraPilot Standard' },
    premium:  { prix: 0, label: 'HoraPilot Premium'  },
  });

  const [logoPreview,          setLogoPreview]          = useState(null);
  const [screenshot1Preview,   setScreenshot1Preview]   = useState(null);
  const [screenshot2Preview,   setScreenshot2Preview]   = useState(null);
  const [uploadingScreenshot1, setUploadingScreenshot1] = useState(false);
  const [uploadingScreenshot2, setUploadingScreenshot2] = useState(false);
  const [horaPilotLogoPreview, setHoraPilotLogoPreview] = useState(null);
  const [signaturePreview,     setSignaturePreview]     = useState(null);
  const [cachetPreview,        setCachetPreview]        = useState(null);
  const [uploading,            setUploading]            = useState(false);
  const [uploadingHoraPilot,   setUploadingHoraPilot]   = useState(false);
  const [uploadingSignature,   setUploadingSignature]   = useState(false);
  const [uploadingCachet,      setUploadingCachet]      = useState(false);
  const [loading,              setLoading]              = useState(true);
  const [savingInfo,           setSavingInfo]           = useState(false);
  const [savingTarifs,         setSavingTarifs]         = useState(false);
  const [okInfo,               setOkInfo]               = useState(false);
  const [okTarifs,             setOkTarifs]             = useState(false);
  const [error,                setError]                = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [sRes, tRes] = await Promise.all([
          superAdminApi.danicedSettings.get(),
          superAdminApi.danicedSettings.getTarifs(),
        ]);
        if (sRes.success && sRes.data && Object.keys(sRes.data).length > 0) {
          setSettings(s => ({ ...s, ...sRes.data }));
          const base = API_URL;
          if (sRes.data.logo_path)           setLogoPreview(`${base}${sRes.data.logo_path}`);
          if (sRes.data.fiche_screenshot_1) setScreenshot1Preview(`${base}${sRes.data.fiche_screenshot_1}`);
          if (sRes.data.fiche_screenshot_2) setScreenshot2Preview(`${base}${sRes.data.fiche_screenshot_2}`);
          if (sRes.data.horapilot_logo_path) setHoraPilotLogoPreview(`${base}${sRes.data.horapilot_logo_path}`);
          if (sRes.data.signature_path)      setSignaturePreview(`${base}${sRes.data.signature_path}`);
          if (sRes.data.cachet_path)         setCachetPreview(`${base}${sRes.data.cachet_path}`);
        }
        if (tRes.success && tRes.data) {
          const map = {};
          tRes.data.forEach(item => { map[item.type_licence] = { prix: item.prix, label: item.label }; });
          setTarifs(prev => ({ ...prev, ...map }));
        }
      } catch (e) { console.error('[DanicedSettings]', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));
  const setTarif = (type, val) => setTarifs(tt => ({ ...tt, [type]: { ...tt[type], prix: val } }));

  const makeUpload = (setPreview, setUpl, apiCall) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Fichier trop volumineux (max 2MB)'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    setUpl(true); setError('');
    try {
      const res = await apiCall(file);
      if (!res.success) setError(res.message || 'Erreur upload');
    } catch { setError("Erreur lors de l'upload"); }
    finally { setUpl(false); }
  };

  const handleScreenshot1Upload   = makeUpload(setScreenshot1Preview, setUploadingScreenshot1, f => superAdminApi.danicedSettings.uploadDocument(f, 'screenshot1'));
  const handleScreenshot2Upload   = makeUpload(setScreenshot2Preview, setUploadingScreenshot2, f => superAdminApi.danicedSettings.uploadDocument(f, 'screenshot2'));
  const handleLogoUpload          = makeUpload(setLogoPreview,          setUploading,          f => superAdminApi.danicedSettings.uploadLogo(f));
  const handleHoraPilotLogoUpload = makeUpload(setHoraPilotLogoPreview, setUploadingHoraPilot, f => superAdminApi.danicedSettings.uploadHoraPilotLogo(f));
  const handleSignatureUpload     = makeUpload(setSignaturePreview,     setUploadingSignature, f => superAdminApi.danicedSettings.uploadDocument(f, 'signature'));
  const handleCachetUpload        = makeUpload(setCachetPreview,        setUploadingCachet,    f => superAdminApi.danicedSettings.uploadDocument(f, 'cachet'));

  const handleSaveInfo = async () => {
    setSavingInfo(true); setError('');
    try {
      const res = await superAdminApi.danicedSettings.update(settings);
      if (res.success) { setOkInfo(true); setTimeout(() => setOkInfo(false), 2500); }
      else setError(res.message || 'Erreur sauvegarde.');
    } catch { setError('Erreur réseau.'); }
    finally { setSavingInfo(false); }
  };

  const handleSaveTarifs = async () => {
    setSavingTarifs(true); setError('');
    try {
      const payload = Object.entries(tarifs).map(([type, v]) => ({ type_licence: type, prix: v.prix, label: v.label }));
      const res = await superAdminApi.danicedSettings.updateTarifs(payload);
      if (res.success) { setOkTarifs(true); setTimeout(() => setOkTarifs(false), 2500); }
      else setError(res.message || 'Erreur sauvegarde.');
    } catch { setError('Erreur réseau.'); }
    finally { setSavingTarifs(false); }
  };

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  const SaveBtn = ({ saving, ok, onClick, label }) => (
    <button onClick={onClick} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 10, background: t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
      {saving ? <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              : ok ? <CheckCircle2 size={15} /> : <Save size={15} />}
      {ok ? 'Enregistré !' : saving ? 'Sauvegarde...' : label}
    </button>
  );

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>

      <div>
        <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Paramètres DANICED GROUP</h1>
        <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>Ces informations apparaissent sur toutes les factures générées</p>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {/* ── SECTION 1 : IDENTITÉ VISUELLE ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={Building2} title="Identité visuelle" sub="Logos et nom du produit affichés en en-tête de facture" theme={t} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${t.border}` }}>
          {/* Logo DANICED */}
          <ImageUploadBlock label="Logo DANICED GROUP" sublabel="Affiché à gauche dans l'en-tête" preview={logoPreview} uploading={uploading} onUpload={handleLogoUpload} inputId="daniced-logo-upload" theme={t} />

          {/* Logo + Nom HoraPilot */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <ImageUploadBlock label="Logo HoraPilot" sublabel="Affiché à droite dans l'en-tête" preview={horaPilotLogoPreview} uploading={uploadingHoraPilot} onUpload={handleHoraPilotLogoUpload} inputId="horapilot-logo-upload" theme={t} accentColor="#448696" />
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>Nom du produit (affiché sous le logo)</label>
              <input value={settings.horapilot_product_name || ''} onChange={e => set('horapilot_product_name', e.target.value)} placeholder="HoraPilot"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, fontWeight: 600, boxSizing: 'border-box', outline: 'none' }}
                onFocus={e => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.1)'; }}
                onBlur={e  => { e.target.style.borderColor = t.border;  e.target.style.boxShadow = 'none'; }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 10, background: t.primarySofter, border: `1px solid ${t.primaryBorder}`, marginBottom: 20 }}>
          <Info size={14} style={{ color: t.primary, flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: t.primary }}>DANICED GROUP</strong> est la holding émettrice —
            <strong style={{ color: '#448696' }}> {settings.horapilot_product_name || 'HoraPilot'}</strong> est le produit facturé. Les deux logos apparaissent côte à côte dans l'en-tête PDF.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Nom de l'entreprise *" value={settings.company_name} onChange={v => set('company_name', v)} placeholder="DANICED GROUP" icon={Building2} theme={t} half />
          <Field label="Slogan / sous-titre" value={settings.slogan} onChange={v => set('slogan', v)} placeholder="Solutions SaaS pour l'Afrique" theme={t} half />
          <Field label="Téléphone principal" value={settings.telephone} onChange={v => set('telephone', v)} placeholder="+242 06..." icon={Phone} theme={t} half />
          <Field label="Téléphone secondaire" value={settings.telephone2} onChange={v => set('telephone2', v)} placeholder="+242 05..." icon={Phone} theme={t} half />
          <Field label="Email" value={settings.email} onChange={v => set('email', v)} placeholder="contact@danicedgroup.com" icon={Mail} theme={t} half />
          <Field label="Site web" value={settings.site_web} onChange={v => set('site_web', v)} placeholder="www.danicedgroup.com" icon={Globe} theme={t} half />
          <Field label="Ville" value={settings.ville} onChange={v => set('ville', v)} placeholder="Brazzaville" icon={MapPin} theme={t} half />
          <Field label="Pays" value={settings.pays} onChange={v => set('pays', v)} placeholder="Congo" theme={t} half />
          <Field label="N° RCCM / Registre" value={settings.numero_registre} onChange={v => set('numero_registre', v)} placeholder="RCCM-BZV-2024-..." icon={FileText} theme={t} half />
          <Field label="Devise" value={settings.devise} onChange={v => set('devise', v)} placeholder="FCFA" theme={t} half />
        </div>
      </div>

      {/* ── SECTION 2 : ADRESSES ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={MapPin} title="Adresses" sub="Adresse Europe et Afrique affichées sur la facture" theme={t} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <TextareaField label="Adresse Europe" value={settings.adresse_europe} onChange={v => set('adresse_europe', v)} placeholder="1 place Giovanni da Verrazano&#10;69009 Lyon, France" rows={3} theme={t} />
          <Field label="Téléphone Europe" value={settings.telephone_europe} onChange={v => set('telephone_europe', v)} placeholder="+33 7 80 73 31 80" icon={Phone} theme={t} />
          <TextareaField label="Adresse Afrique" value={settings.adresse_afrique} onChange={v => set('adresse_afrique', v)} placeholder="Pointe-Noire, République du Congo..." rows={3} theme={t} />
          <Field label="Téléphone Afrique" value={settings.telephone_afrique} onChange={v => set('telephone_afrique', v)} placeholder="+242 06 985 92 19" icon={Phone} theme={t} />
        </div>
      </div>

      {/* ── SECTION 3 : INTERLOCUTEUR ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={User} title="Interlocuteur DANICED" sub="Contact qui représente votre entreprise sur les factures" theme={t} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Nom de l'interlocuteur" value={settings.interlocuteur} onChange={v => set('interlocuteur', v)} placeholder="Cédric DANIGO" icon={User} theme={t} half />
          <Field label="Titre / Fonction" value={settings.interlocuteur_titre} onChange={v => set('interlocuteur_titre', v)} placeholder="Directeur Commercial" theme={t} half />
        </div>
      </div>

      {/* ── SECTION 4 : SUPPORT ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={Headphones} title="Contact support" sub="Nom du service et contacts affichés en pied de facture" theme={t} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Nom du service support" value={settings.support_name} onChange={v => set('support_name', v)} placeholder="Support HoraPilot" icon={Headphones} theme={t} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Téléphone support" value={settings.support_phone} onChange={v => set('support_phone', v)} placeholder="+242 06..." icon={Phone} theme={t} half />
            <Field label="Email support" value={settings.support_email} onChange={v => set('support_email', v)} placeholder="support@horapilot.com" icon={Mail} theme={t} half />
          </div>
        </div>
      </div>

      {/* ── SECTION 5 : MODES DE PAIEMENT ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={CreditCard} title="Modes de paiement" sub="Liste des moyens de paiement proposés sur les factures" theme={t} />
        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 10, background: t.primarySofter, border: `1px solid ${t.primaryBorder}`, marginBottom: 18 }}>
          <Info size={14} style={{ color: t.primary, flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
            Au moment de créer une facture, vous choisissez quel mode afficher. Chaque mode a son propre numéro/RIB et ses instructions spécifiques.
          </p>
        </div>
        <PaymentMethodsManager theme={t} />
      </div>

      {/* ── SECTION 6 : SIGNATURE + CACHET ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={Stamp} title="Signature & Cachet électroniques" sub="Affichés côte à côte en bas de chaque facture PDF" theme={t} />
        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 20 }}>
          <Info size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
            Importez vos images PNG avec fond transparent. La <strong>signature</strong> apparaîtra à gauche et le <strong>cachet</strong> à droite en bas de la facture.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <ImageUploadBlock label="Signature électronique" sublabel="PNG transparent · max 2 Mo" preview={signaturePreview} uploading={uploadingSignature} onUpload={handleSignatureUpload} inputId="signature-upload" theme={t} accentColor="#6366f1" size={120} />
          <ImageUploadBlock label="Cachet / Tampon" sublabel="PNG transparent · max 2 Mo" preview={cachetPreview} uploading={uploadingCachet} onUpload={handleCachetUpload} inputId="cachet-upload" theme={t} accentColor="#f59e0b" size={120} />
        </div>
      </div>

      {/* ── SECTION 7 : FICHE PRÉSENTATION ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={FileText} title="Fiche Présentation" sub="Textes affichés sur la fiche PDF de présentation HoraPilot" theme={t} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Accroche principale (tagline)" value={settings.fiche_tagline} onChange={v => set('fiche_tagline', v)} placeholder="Pilotez votre activité, gérez vos équipes" icon={FileText} theme={t} />
          <Field label="Texte CTA (appel à l'action)" value={settings.fiche_cta} onChange={v => set('fiche_cta', v)} placeholder="Demandez votre démo — Sans engagement · Déploiement rapide" icon={FileText} theme={t} />
          <TextareaField label="Texte de présentation générale" value={settings.fiche_presentation} onChange={v => set('fiche_presentation', v)} placeholder="HoraPilot est une plateforme SaaS..." rows={4} theme={t} />
          <TextareaField label="Pour qui (secteurs cibles)" value={settings.fiche_pour_qui} onChange={v => set('fiche_pour_qui', v)} placeholder="BTP · Commerce · Santé · Éducation..." rows={4} theme={t} />
        </div>
        {/* Screenshots interface */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${t.border}` }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: t.textSecondary, marginBottom: 14 }}>
            📸 Visuels de l'interface (affichés dans le mockup tablette de la fiche)
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ImageUploadBlock
              label="Screenshot principal (grand mockup)"
              sublabel="PNG/JPG · Vue dashboard ou pointage · max 2 Mo"
              preview={screenshot1Preview}
              uploading={uploadingScreenshot1}
              onUpload={handleScreenshot1Upload}
              inputId="screenshot1-upload"
              theme={t}
              accentColor={t.primary}
              size={140}
            />
            <ImageUploadBlock
              label="Screenshot secondaire (overlay)"
              sublabel="PNG/JPG · Vue complémentaire · max 2 Mo"
              preview={screenshot2Preview}
              uploading={uploadingScreenshot2}
              onUpload={handleScreenshot2Upload}
              inputId="screenshot2-upload"
              theme={t}
              accentColor="#8b5cf6"
              size={140}
            />
          </div>
        </div>
      </div>

      {/* ── SECTION 8 : FACTURATION ── */}
      <div style={{ padding: '22px 24px', borderRadius: 16, background: t.bgCard, border: `1px solid ${t.border}` }}>
        <SectionHeader icon={FileText} title="Paramètres de facturation" sub="Préfixe, devise et mentions légales" theme={t} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Préfixe numéro de facture" value={settings.invoice_prefix} onChange={v => set('invoice_prefix', v)} placeholder="INV" theme={t} half />
          <Field label="Devise" value={settings.devise} onChange={v => set('devise', v)} placeholder="FCFA" theme={t} half />
          <TextareaField label="Mentions légales (pied de facture)" value={settings.mentions_legales} onChange={v => set('mentions_legales', v)} placeholder="Conformément à la clause..." rows={3} theme={t} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SaveBtn saving={savingInfo} ok={okInfo} onClick={handleSaveInfo} label="Sauvegarder les informations" />
      </div>

      {/* Section Tarifs — voir Modules & Licences */}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default DanicedSettings;
